import { useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Button, Spinner } from 'aimo-ui';
import PaymentDetails from 'components/account/parking/PaymentDetails';
import SessionDetails from 'components/account/parking/SessionDetails';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import AimoMarker from 'components/map/AimoMarker';
import MapComponent from 'components/map/MapComponent';
import { Center } from 'components/park/styles/styles';
import ParkingBadge from 'components/receipt/ParkingBadge';
import Sheet from 'components/sheets/Sheet';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { READ_PARKING_SESSION } from 'graphql/queries/readParkingSession';
import { READ_RECEIPT_BY_TRANSACTION_ID } from 'graphql/queries/readReceiptByTransactionId';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useCreateExternalPayment from 'hooks/useCreateExternalPayment';
import useParkingZoneWithEVCheck from 'hooks/useParkingZoneWithEVCheck';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import useUpdateParkingMutation from 'hooks/useUpdateParkingMutation';
import SafeAreaHeaderContainer from 'navigation/SafeAreaHeaderContainer';
import React, { ReactNode, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useMapStore } from 'stores/mapStore';
import { useParkingStore } from 'stores/parkingStore';
import { useRetryPaymentStore } from 'stores/retryPaymentStore';
import { Divider } from 'styles/CommonLayoutStyles';
import {
  ButtonContainer,
  ColumnWithMargins,
  ScreenContainer,
} from 'styles/ContainerStyles';
import {
  BillingItem,
  ParkingSession,
  ParkZone,
  Query,
  QueryReadParkingSessionArgs,
  QueryReadReceiptByTransactionIdArgs,
  Receipt,
  UpdateParkingSessionActionType,
} from 'types/generatedSchemaTypes';
import {
  AccountScreenNavigationProp,
  ParkingDetailsNavigationProp,
} from 'types/navigation';
import { getCorrectBillingItem } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { makePoint } from 'utils/mapUtils';
import { getLocationIconType } from 'utils/parkUtils';
import {
  canOpenExternalPaymentApp,
  isExternalPayment,
} from 'utils/paymentUtils';
import ExportPdfButton from '../../components/receipt/ExportPdfButton';

const useParkingSession = (sessionId?: string) => {
  return useQuery<Query, QueryReadParkingSessionArgs>(READ_PARKING_SESSION, {
    variables: {
      sessionId: sessionId as string,
    },
    fetchPolicy: 'cache-and-network',
    skip: !sessionId,
  });
};

const useParkingReceipt = (transactionId?: string, skip?: boolean) => {
  return useQuery<Query, QueryReadReceiptByTransactionIdArgs>(
    READ_RECEIPT_BY_TRANSACTION_ID,
    {
      variables: {
        transactionId: transactionId as string,
      },
      skip: !transactionId || skip,
    }
  );
};

export const WebSheetWrapper = ({
  children,
  BottomComponent,
  showRetryButton,
}: {
  children: ReactNode;
  BottomComponent: React.ReactNode;
  showRetryButton: boolean;
}) => {
  if (isWeb) {
    return (
      <Sheet
        snappingPoints={['80vh']}
        hideHandle
        stickyFooter={showRetryButton && BottomComponent}>
        {children}
      </Sheet>
    );
  }
  return <>{children}</>;
};

type RetryButtonState = 'loading' | 'pending' | 'success' | 'failed';

const CheckIcon = () => <Icon name="check" />;

const RetryButton = ({
  onRetry,
  retryButtonState,
  text,
}: {
  onRetry: () => void;
  retryButtonState: RetryButtonState | undefined;
  text: string;
}) => {
  return (
    <ButtonContainer>
      <Button
        text={text}
        size="l"
        testID="retry"
        buttonType="primary"
        onPress={onRetry}
        loading={retryButtonState === 'loading'}
        disabled={retryButtonState !== 'failed'}
        iconComponent={retryButtonState === 'pending' ? CheckIcon : undefined}
        iconRight={retryButtonState === 'pending'}
      />
    </ButtonContainer>
  );
};
const { height } = Dimensions.get('window');

const ParkingHistoryDetailsScreen: React.FC<{
  showBeforeMapLoaded?: boolean;
}> = ({ showBeforeMapLoaded = false }) => {
  const { t } = useTranslation();
  const {
    flags: { anpr },
  } = useContext(FeatureFlagsContext);
  const showCameraRecognition = showFeatureFlag(anpr);
  const paymentMethod = useSelectedPaymentMethod();
  const tabBarHeight = useBottomMenuHeight();

  const { params } = useRoute<ParkingDetailsNavigationProp['route']>();
  const { sessionId, parkingZoneUid, transactionId } = params;

  const externalPaymentMethod = isExternalPayment(paymentMethod);

  const {
    data: parkingSessionData,
    loading: parkingSessionLoading,
    error: parkingSessionError,
  } = useParkingSession(sessionId);

  const paymentStateFromSession =
    parkingSessionData?.readParkingSession?.paymentState;
  const sessionTransactionId =
    parkingSessionData?.readParkingSession?.transactionId;
  // When navigating from active/recent session list, transactionId is not in params — fall back to the session's own transactionId.
  const effectiveTransactionId =
    transactionId || sessionTransactionId || undefined;
  const {
    data: parkingReceiptData,
    loading: parkingReceiptLoading,
    error: parkingReceiptError,
  } = useParkingReceipt(
    effectiveTransactionId,
    !!sessionId && paymentStateFromSession === 'Failed'
  );

  const parkingSessionId =
    parkingReceiptData?.readReceiptByTransactionId?.items?.[0]
      ?.parkingSessionId;

  const parkingZoneResult = useParkingZoneWithEVCheck(
    parkingZoneUid || '',
    'cache-and-network',
    false
  );
  const {
    data: parkZoneData,
    loading: parkZoneLoading,
    error: parkZoneError,
  } = parkingZoneResult;

  const retryParkingParams: {
    sessionId: string;
    actionType: UpdateParkingSessionActionType;
  } = {
    sessionId: sessionId as string,
    actionType: 'RETRY_PAYMENT',
  };

  const { startPolling: startRetryPolling, getSessionStatus } =
    useRetryPaymentStore();

  const { navigate } = useNavigation<AccountScreenNavigationProp>();

  const onRetrySuccess = () => {
    startRetryPolling(sessionId as string);
    navigate('accountScreen', {
      screen: 'parkingHistory',
      params: { filter: 'failed' },
    });
  };

  const { updateParking, updateParkingLoading } = useUpdateParkingMutation({
    ...retryParkingParams,
    callback: onRetrySuccess,
  });

  const { createExternalPayment } = useCreateExternalPayment({
    onFreeComplete: () => Promise.resolve(),
    onRetryComplete: onRetrySuccess,
  });

  let loading, error;

  if (sessionId) {
    loading = parkingSessionLoading;
    error = parkingSessionError;
  } else {
    loading = parkingReceiptLoading || parkZoneLoading || updateParkingLoading;
    error = parkingReceiptError || parkZoneError;
  }

  const { address, locationType, parkingMethods, commercialZoneView } =
    parkZoneData?.parkZone || {};
  const { paymentState } = parkingSessionData?.readParkingSession || {};

  const sessionRetryStatus = getSessionStatus(
    sessionId as string,
    paymentState
  );

  const retryButtonState: RetryButtonState | undefined = (() => {
    if (updateParkingLoading) return 'loading';
    switch (sessionRetryStatus) {
      case 'Success':
        return 'success';
      case 'Failed':
        return 'failed';
      case 'Retrying':
      case 'Pending':
        return 'pending';
      default:
        return undefined;
    }
  })();

  useEffect(() => {
    useParkingStore.getState().setSelectedParkingSession(sessionId ?? null);
    return () => {
      useParkingStore.getState().setSelectedParkingSession(null);
    };
  }, [sessionId]);

  useEffect(() => {
    let timeoutHandler: any;
    if (address?.marker?.coordinates) {
      const coordinates = makePoint(address?.marker?.coordinates);
      timeoutHandler = setTimeout(
        () => useMapStore.getState().setMapCoordinates(coordinates),
        500
      );
    }
    return () => {
      clearTimeout(timeoutHandler);
    };
  }, [address?.marker?.coordinates]);

  useEffect(() => {
    if (error) {
      showToast(error.message || t('error.genericError'), 'error');
    }
  }, [error, t]);

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const retryButtonTextMap: Record<RetryButtonState, string> = {
    loading: t('button.retryPayment'),
    pending: t('button.paymentQueued'),
    success: t('button.paymentSuccess'),
    failed: t('button.retryPayment'),
  };

  const onPaymentRetry = async () => {
    if (!sessionId) {
      return;
    }

    if (!externalPaymentMethod) {
      return updateParking({
        variables: {
          input: {
            ...retryParkingParams,
            paymentMethodType: paymentMethod.method,
            cardId: paymentMethod?.cardId as string | undefined,
          },
        },
      });
    }

    if (!(await canOpenExternalPaymentApp())) {
      return showToast(
        t('parking.parkingProcess.externalAppOpenFailed', {
          provider: paymentMethod?.externalPaymentType,
        }),
        'error'
      );
    }
    const description = t('parking.externalParkingDescription', {
      zone: commercialZoneView?.zoneCode,
      licencePlate: licensePlate,
    });
    return createExternalPayment({
      variables: {
        input: {
          actionType: 'RETRY_PAYMENT',
          orderDescription: description,
          paymentMethod: paymentMethod?.externalPaymentType,
          items: [
            {
              parkingSessionId: sessionId || '',
            },
          ],
        },
      },
    });
  };

  const isPaymentFailed =
    !!sessionId &&
    (sessionRetryStatus === 'Failed' ||
      sessionRetryStatus === 'Retrying' ||
      sessionRetryStatus === 'Pending');

  const showRetryButton = isPaymentFailed;

  const licensePlate =
    parkingSessionData?.readParkingSession?.accessDevice?.value ||
    getCorrectBillingItem(
      parkingReceiptData?.readReceiptByTransactionId?.items as
        | [BillingItem]
        | undefined
    )?.[0].licensePlate ||
    '';
  const markerType = getLocationIconType(
    locationType,
    showCameraRecognition && parkingMethods?.anpr
  );
  const Markers = address?.marker?.coordinates ? (
    <AimoMarker
      key={`parking-spot-${parkingZoneUid}`}
      coordinate={makePoint(address?.marker?.coordinates)}
      markerType={markerType}
      activeIndex={1}
      onPress={() => undefined}
      testID={`${markerType}-marker-${parkingZoneUid}`}
      id={parkingZoneUid}
      isSelected={true}
    />
  ) : undefined;

  const marginBottom = () => {
    if (showRetryButton) {
      return 85 + tabBarHeight;
    } else {
      return 20 + tabBarHeight;
    }
  };

  const showExportPdfButton = !isPaymentFailed && !!parkingSessionId;
  return (
    <>
      {isWeb && (
        <ScreenContainer>
          <MapComponent
            showBeforeMapLoaded={showBeforeMapLoaded}
            showLocation={false}
            showLogo={false}
            mapPadding={{ top: 0, left: 0, right: 0, bottom: height * 0.62 }}
            scrollEnabled={false}>
            {Markers}
          </MapComponent>
          <ParkingBadge
            licensePlate={licensePlate}
            testID="parking-history-details-info"
          />
        </ScreenContainer>
      )}
      <WebSheetWrapper
        BottomComponent={
          <RetryButton
            onRetry={onPaymentRetry}
            retryButtonState={retryButtonState}
            text={retryButtonState ? retryButtonTextMap[retryButtonState] : ''}
          />
        }
        showRetryButton={showRetryButton}>
        {!isWeb && (
          <SafeAreaHeaderContainer extraPadding="all">
            <ColumnWithMargins mt={10} mb={10}>
              <ParkingBadge
                licensePlate={licensePlate}
                testID="parking-history-details-info"
                noMap
              />
            </ColumnWithMargins>
          </SafeAreaHeaderContainer>
        )}
        <ScrollView>
          <ColumnWithMargins ml={20} mr={20}>
            <SessionDetails
              testID="parking-summary"
              parkingSession={
                parkingSessionData?.readParkingSession as ParkingSession
              }
              receiptData={
                parkingReceiptData?.readReceiptByTransactionId as Receipt
              }
              parkZone={parkZoneData?.parkZone as ParkZone}
            />
          </ColumnWithMargins>
          <Divider size={10} marginVertical={30} />
          <ColumnWithMargins ml={20} mr={20} mb={marginBottom()}>
            <PaymentDetails
              testID="payment-summary"
              receiptData={
                parkingReceiptData?.readReceiptByTransactionId as Receipt
              }
              parkingSession={
                parkingSessionData?.readParkingSession as ParkingSession
              }
              isPaymentFailed={isPaymentFailed}
              transactionId={transactionId}
            />
            {showExportPdfButton && (
              <ExportPdfButton
                isPaymentFailed={isPaymentFailed}
                sessionId={parkingSessionId}
              />
            )}
            {showRetryButton && !isWeb && (
              <RetryButton
                onRetry={onPaymentRetry}
                retryButtonState={retryButtonState}
                text={
                  retryButtonState ? retryButtonTextMap[retryButtonState] : ''
                }
              />
            )}
          </ColumnWithMargins>
        </ScrollView>
      </WebSheetWrapper>
    </>
  );
};

export default ParkingHistoryDetailsScreen;
